// ** React Imports
import { Suspense, useContext, lazy, Fragment, useState } from 'react';

// ** Utils
import { isUserLoggedIn } from '@utils';
import { useLayout } from '@hooks/useLayout';
import { AbilityContext } from '@src/utility/context/Can';
import { useRouterTransition } from '@hooks/useRouterTransition';

// ** Custom Components
import LayoutWrapper from '@layouts/components/layout-wrapper';

// ** Router Components
import {BrowserRouter as AppRouter, Route, Switch, Redirect, Link} from 'react-router-dom';

// ** Routes & Default Routes
import { DefaultRoute, Routes } from './routes';

// ** Layouts
import BlankLayout from '@layouts/BlankLayout';
import VerticalLayout from '@src/layouts/VerticalLayout';
import HorizontalLayout from '@src/layouts/HorizontalLayout';

import { getOnboardingStatus } from '../utility/Utils';
import { useDispatch } from 'react-redux';
import { handleLogout } from '../redux/authentication';
import {Home, Layers, Users} from "react-feather";
import {BiCalendarCheck, BiSolidZap} from "react-icons/bi";
import {CgShoppingCart} from "react-icons/cg";
import {FaFacebookMessenger} from "react-icons/fa";
import {HiOutlineUserGroup} from "react-icons/hi";
import {isMobile} from "react-device-detect";
import '../assets/styles/sidebar.scss'

const menuItems = [
  {
    title: 'Home',
    icon: <Home size={24}/>,
    path: '/community',
  },
  {
    title: 'Dashboard',
    icon: <Layers size={24}/>,
    path: '/community/dashboard',
  },
  {
    title: 'Events',
    icon: <BiSolidZap size={24}/>,
    path: '/community/events',
  },
  {
    title: 'Shop',
    icon: <CgShoppingCart size={24}/>,
    path: '/community/shop',
  },
  {
    title: 'Message',
    icon: <FaFacebookMessenger size={24}/>,
    path: '/community/message',
  },
  {
    title: 'Calendar',
    icon: <BiCalendarCheck size={24}/>,
    path: '/community/calendar',
  },
  {
    title: 'Members',
    icon: <Users size={24}/>,
    path: '/community/members',
  },
  {
    title: 'Group',
    icon: <HiOutlineUserGroup size={24}/>,
    path: '/community/group',
  }
]

const Router = () => {
  // ** Hooks
  const { layout, setLayout, setLastLayout } = useLayout();
  const { transition, setTransition } = useRouterTransition();
  const [activeMenu, setActiveMenu] = useState('Home');

  // ** ACL Ability Context
  const ability = useContext(AbilityContext);
  const dispatch = useDispatch()

  // ** Default Layout
  const DefaultLayout = layout === 'horizontal' ? 'HorizontalLayout' : 'VerticalLayout';

  // ** All of the available layouts
  const Layouts = { BlankLayout, VerticalLayout, HorizontalLayout };

  // ** Current Active Item
  const currentActiveItem = null;

  // ** Return Filtered Array of Routes & Paths
  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = [];
    const LayoutPaths = [];

    if (Routes) {
      Routes.filter((route) => {
        // ** Checks if Route layout or Default layout matches current layout
        if (route.layout === layout || (route.layout === undefined && DefaultLayout === layout)) {
          LayoutRoutes.push(route);
          LayoutPaths.push(route.path);
        }
      });
    }

    return { LayoutRoutes, LayoutPaths };
  };

  const NotAuthorized = lazy(() => import('@src/views/pages/misc/NotAuthorized'));

  // const NotAuthorizedActivateEmployeeView = lazy(() =>
  //   import('@src/views/calendar/event/preview/NotAuthorizedActivateEmployeeView')
  // );
  // const AuthorizedPunchEmployeeView = lazy(() =>
  //   import('@src/views/contacts/checkin/AuthorizedPunchEmployeeView')
  // );

  // ** Init Error Component
  const Error = lazy(() => import('@src/views/pages/misc/Error'));
  const Video = lazy(() => import('@src/views/pages/misc/Video'));

  /**
   ** Final Route Component Checks for Login & User Role and then redirects to the route
   */
  const FinalRoute = (props) => {
    const route = props.route;
    let action, resource;
    // ** Assign vars based on route meta
    if (route.meta) {
      action = route.meta.action ? route.meta.action : null;
      resource = route.meta.resource ? route.meta.resource : null;
    }
    if(props.match.path === '/login/:eventId'){
      //dispatch(handleLogout())
      //return <Redirect to={`${props.match.url}`} />;
      console.log("INSIDE")
      return <route.component {...props} />;
    }
    else if (
      (!isUserLoggedIn() && route.meta === undefined) ||
      (!isUserLoggedIn() && route.meta && !route.meta.authRoute && !route.meta.publicRoute)
    ) {
      // if(window.location.href.includes('login') ||
      // window.location.href.includes('signup') ||
      // window.location.href.includes('register')){
      //   return <Redirect to={window.location.href.split('.com')[1]} />;
      // }
      // else{
        
      // }
      //http://localhost:3001/login/66eafcb440c69469b9bac238
     
      return <Redirect to="/login" />;
      /**
       ** If user is not Logged in & route meta is undefined
       ** OR
       ** If user is not Logged in & route.meta.authRoute, !route.meta.publicRoute are undefined
       ** Then redirect user to login
       */
      
    } 
    // else if(route?.route?.path ==='/login/:eventId'){
    //   return <Redirect to={props.match.url} />
    // }
    else if (route.meta && route.meta.authRoute && isUserLoggedIn()) {
      // ** If route has meta and authRole and user is Logged in then redirect user to home page (DefaultRoute)
      return <Redirect to="/" />;
    } else if (isUserLoggedIn() && !ability.can('read', 'base')) {
      // ** If user is Logged in and doesn't have ability to visit the page redirect the user to Not Authorized
      return <Redirect to="/misc/not-authorized" />;
    } else {
      // ** If none of the above render component
      return <route.component {...props} />;
    }
  };

  // ** Return Route to Render
  const ResolveRoutes = () => {
    return Object.keys(Layouts).map((layout, index) => {
      // ** Convert Layout parameter to Layout Component
      // ? Note: make sure to keep layout and component name equal

      const LayoutTag = Layouts[layout];

      // ** Get Routes and Paths of the Layout
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(layout);

      // ** We have freedom to display different layout for different route
      // ** We have made LayoutTag dynamic based on layout, we can also replace it with the only layout component,
      // ** that we want to implement like VerticalLayout or HorizontalLayout
      // ** We segregated all the routes based on the layouts and Resolved all those routes inside layouts

      // ** RouterProps to pass them to Layouts
      const routerProps = {};

      return (
        <Route path={LayoutPaths} key={index}>
          <LayoutTag
            layout={layout}
            setLayout={setLayout}
            transition={transition}
            routerProps={routerProps}
            setLastLayout={setLastLayout}
            setTransition={setTransition}
            currentActiveItem={currentActiveItem}
          >

            <Switch>
              {LayoutRoutes.map((route) => {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact === true}
                    render={(props) => {
                      // ** Assign props to routerProps
                      Object.assign(routerProps, {
                        ...props,
                        meta: route.meta
                      });

                      return (
                        <Fragment>
                          {/* Layout Wrapper to add classes based on route's layout, appLayout and className */}

                          {route.layout === 'BlankLayout' ? (
                            <Fragment>
                              <FinalRoute route={route} {...props} />
                            </Fragment>
                          ) : (
                            <div style={!isMobile && {display: 'grid', gridTemplateColumns: '250px auto', gap: '20px', width: '100%'} || {}}>
                              {!isMobile && (
                                <div style={{
                                  height: '100%',
                                  minHeight: '100dvh',
                                  padding: '20px',
                                  background: '#ffffff',
                                  marginTop: '45px'
                                }}>
                                  <div className="sidebar-menu">
                                    {menuItems?.map((item, index) => (
                                      <Link
                                        key={index}
                                        to={item.path}
                                        onClick={() => {
                                          setActiveMenu(item.title);
                                        }}
                                        className={`sidebar-item ${activeMenu === item.title ? 'active' : ''}`}
                                      >
                                        <div
                                          className="sidebar-content"
                                          onClick={() => {

                                          }}
                                        >
                                          {item.icon}
                                          <span className="sidebar-text">{item?.title}</span>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <LayoutWrapper
                                layout={DefaultLayout}
                                transition={transition}
                                setTransition={setTransition}
                                /* Conditional props */
                                /*eslint-disable */
                                {...(route.appLayout
                                  ? {
                                    appLayout: route.appLayout
                                  }
                                  : {})}
                                {...(route.meta
                                  ? {
                                    routeMeta: route.meta
                                  }
                                  : {})}
                                {...(route.className
                                  ? {
                                    wrapperClass: route.className
                                  }
                                  : {})}
                                /*eslint-enable */
                              >

                                <Suspense fallback={null}>
                                  <FinalRoute route={route} {...props} />
                                </Suspense>
                              </LayoutWrapper>
                            </div>
                          )}
                        </Fragment>
                      );
                    }}
                  />
                );
              })}
            </Switch>


          </LayoutTag>
        </Route>
      );
    });
  };

  return (
    <AppRouter basename={process.env.REACT_APP_BASENAME}>
      <Switch>
        {/* If user is logged in Redirect user to DefaultRoute else to login */}
        <Route
          exact
          path="/"
          render={() => {
            let isOnboarded = true;
            if (!getOnboardingStatus()) isOnboarded = false;
            else {
              Object.keys(getOnboardingStatus()).forEach((criteria) => {
                if (!criteria) {
                  isOnboarded = false;
                  return;
                }
              });
            }
            return isUserLoggedIn() ? (
              // <>
              //   {userType === 'super-admin' ? (
              <Redirect to={isOnboarded ? DefaultRoute : '/community'} />
            ) : (
              //   ) : (
              //     <OgranizationMain
              //       isOnboarded={isOnboarded}
              //       DefaultRoute={DefaultRoute}
              //       setUserType={setUserType}
              //     />
              //   )}
              // </>
              <>
                {window.location.href.includes('login') ||
                window.location.href.includes('signup') ||
                window.location.href.includes('register') ? (
                  <Redirect to={window.location.href.split('.com')[1]} />
                ) : (
                  <Redirect to="/login" />
                )}
              </>
            );
          }}
        />
        {/* Not Auth Route */}
        <Route
          exact
          path="/misc/not-authorized"
          render={() => (
            <Layouts.BlankLayout>
              <NotAuthorized />
            </Layouts.BlankLayout>
          )}
        />

        {/* Not Authorized Verify Page */}
        {isUserLoggedIn() && (
          <Route
            exact
            path="/employee/checkin"
            render={() => (
              <Layouts.BlankLayout>
                <AuthorizedPunchEmployeeView />
              </Layouts.BlankLayout>
            )}
          />
        )}

        {ResolveRoutes()}

        {/* NotFound Error page */}
        <Route path="/watchVideo/:token" component={Video} />
        <Route path="*" component={Error} />
      </Switch>
    </AppRouter>
  );
};

export default Router;
